/**
 * Product photos for the purchase-order PDF's Photo column.
 *
 * The images live on R2, which sends no CORS headers, so the page can show
 * them but html2canvas can't draw them into the PDF. They're fetched
 * through the API instead (GET /products/{id}/images/{image_id}/file, the
 * 200 px thumbnail) and turned into small square data URLs.
 */
import type { PurchaseItem } from "../../services/procurementService";
import { productImageService } from "../../services/productImageService";
import { primaryImage } from "./layout";

/** Thumbnail edge in pixels: the 40 px tile drawn at 2× plus a little. */
const THUMB_PX = 96;
const PER_IMAGE_TIMEOUT_MS = 10000;
const PARALLEL = 6;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("timeout")), ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });

/** The photo fitted (not cropped) into a white square, as a JPEG data URL. */
async function squareJpeg(blob: Blob): Promise<string> {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = THUMB_PX;
    canvas.height = THUMB_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, THUMB_PX, THUMB_PX);
    const scale = Math.min(THUMB_PX / bitmap.width, THUMB_PX / bitmap.height);
    const w = bitmap.width * scale;
    const h = bitmap.height * scale;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, (THUMB_PX - w) / 2, (THUMB_PX - h) / 2, w, h);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.88);
}

/**
 * product_id → thumbnail data URL, for every product on the order that has
 * a photo. A photo that fails or takes too long is left out; its row shows
 * an empty tile and the PDF still saves.
 */
export async function loadThumbnails(items: PurchaseItem[]): Promise<Map<string, string>> {
    const wanted = new Map<string, string>();
    items.forEach((item) => {
        const image = primaryImage(item.product?.images);
        if (item.product && image) wanted.set(item.product.product_id, image.image_id);
    });

    const thumbnails = new Map<string, string>();
    const queue = Array.from(wanted);
    const worker = async () => {
        for (let next = queue.shift(); next; next = queue.shift()) {
            const [productId, imageId] = next;
            try {
                const blob = await withTimeout(
                    productImageService.getImageBlob(productId, imageId, "thumbnail"),
                    PER_IMAGE_TIMEOUT_MS,
                );
                thumbnails.set(productId, await squareJpeg(blob));
            } catch {
                // Left out on purpose — see above.
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
    return thumbnails;
}
